import React, { useContext, useEffect } from 'react';
import { IconLogoGoldie, IconLogoOutlook, IconListChecks, IconCheckCircle } from '@sm/wds-icons';

import { Box } from '@wds/box';
import { Grid } from '@wds/grid';
import { List } from '@wds/list';
import { Typography } from '@wds/typography';

import { StaticContext } from '@sm/webassets';

import DeveloperWebBasePage from '../DeveloperWebBasePage';

import waitForUsabilla from '~app/helpers/waitForUsabilla';

import AppTable from '../../components/AppTable';

import './build-a-public-app.scss';

const renderAppChecklist = () => {
  return (
    <List noRules transparent dense>
      <List.Item
        addOnLeft={
          <span className="green-icon">
            <IconCheckCircle />
          </span>
        }
      >
        <Typography variant="body">
          Your app should have unique value and be attracting new users regularly.{' '}
        </Typography>
      </List.Item>
      <List.Item
        addOnLeft={
          <span className="green-icon">
            <IconCheckCircle />
          </span>
        }
      >
        <Typography variant="body">
          Your app should NOT replicate functionality already offered by SurveyMonkey&apos;s survey tools.{' '}
        </Typography>
      </List.Item>
      <List.Item
        addOnLeft={
          <span className="green-icon">
            <IconCheckCircle />
          </span>
        }
      >
        <Typography variant="body">
          Your app&apos;s name should NOT be a generic term (i.e. &quot;Analytics&quot;), include versioning (i.e.
          &quot;App V3.0&quot;), and/or include the word &quot;SurveyMonkey&quot;.{' '}
        </Typography>
      </List.Item>
      <List.Item
        addOnLeft={
          <span className="green-icon">
            <IconCheckCircle />
          </span>
        }
      >
        <Typography variant="body">
          Your app should have at least five OAuth sign ins in the first three months.{' '}
        </Typography>
      </List.Item>
      <List.Item
        addOnLeft={
          <span className="green-icon">
            <IconCheckCircle />
          </span>
        }
      >
        <Typography variant="body">
          You must state if users will need a free or paid account from your service to use your app.{' '}
        </Typography>
      </List.Item>
      <List.Item
        addOnLeft={
          <span className="green-icon">
            <IconCheckCircle />
          </span>
        }
      >
        <Typography variant="body">Your team must provide easily-accessible app support.</Typography>
      </List.Item>
      <List.Item
        addOnLeft={
          <span className="green-icon">
            <IconCheckCircle />
          </span>
        }
      >
        <Typography variant="body">
          All communication should be over HTTPS using a valid SSL certificate (we value security!)
        </Typography>
      </List.Item>
    </List>
  );
};

const renderListingChecklist = () => {
  return (
    <List noRules transparent dense>
      <List.Item>
        <Typography variant="body">
          Listings help users gain a better understanding of your app&apos;s core functionality and scope requirements.
          Therefore, they should be written honestly and truthfully.{' '}
        </Typography>
      </List.Item>
      <List.Item>
        <Typography variant="body">
          Visit the listings tab in the My Apps view to create an app listing and submit it for approval.{' '}
        </Typography>
      </List.Item>
      <List.Item>
        <Typography variant="body">
          Your first app listing should be in English, but we encourage you to make additional app listings in other
          languages.{' '}
        </Typography>
      </List.Item>
    </List>
  );
};

const BuildAPublicApp = () => {
  const {
    environment: { subdomain },
  } = useContext(StaticContext);

  useEffect(() => {
    waitForUsabilla(() => {
      window.usabilla_live('show');
    });
  }, []);

  const prependUrl = subdomain !== 'developer' && subdomain !== 'developer.eu' ? '/developer' : '';

  return (
    <div className="container-build-a-public-app" data-testid="BuildAPublicApp__Developer">
      <DeveloperWebBasePage id="Developer - BuildAPublicApp" title="SurveyMonkey API Developer Portal">
        <div className="build-a-public-app-page">
          <Box mb={6}>
            <Box my={8}>
              <Box mb={4}>
                <Typography variant="pageTitle" component="h1" align="center">
                  Creating a Public App
                </Typography>
              </Box>
              <Box>
                <Typography variant="body" align="center">
                  Public apps can be listed on SurveyMonkey&apos;s App Directory after meeting certain criteria and
                  receiving approval.
                  <br />
                  Some examples may include:
                </Typography>
              </Box>
            </Box>
            <Box mb={6}>
              <Grid>
                <Grid.Item>
                  <div className="public-app-icon">
                    <IconLogoGoldie />
                  </div>
                  <Box>
                    <Typography variant="body" align="center">
                      A service that offers complementary functionality to SurveyMonkey
                    </Typography>
                  </Box>
                </Grid.Item>
                <Grid.Item>
                  <div className="public-app-icon">
                    <IconLogoOutlook />
                  </div>
                  <Box>
                    <Typography variant="body" align="center">
                      An integration of SurveyMonkey functionality into a pre-existing tool
                    </Typography>
                  </Box>
                </Grid.Item>
                <Grid.Item>
                  <div className="public-app-icon">
                    <IconListChecks />
                  </div>
                  <Box>
                    <Typography variant="body" align="center">
                      A popular app that extends SurveyMonkey&apos;s functionality
                    </Typography>
                  </Box>
                </Grid.Item>
              </Grid>
            </Box>

            <Box mb={6}>
              <Box mb={6}>
                <Typography variant="body" align="center">
                  Public apps have different guidelines than apps built for private use. Here are a few key differences:
                </Typography>
              </Box>

              <AppTable />

              <Box mb={2} mt={4}>
                <Typography variant="body">
                  Think your app is better suited to be Private? Check out{' '}
                  <a href={`${prependUrl}/build-a-private-app/`}>Creating a Private App</a>.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body">
                  *Please see our <a href="/tou/">API Developer Terms</a> for more guidelines when building with our
                  API.
                </Typography>
              </Box>

              <Box mt={8} mb={2}>
                <Typography variant="pageTitle" component="h1" align="center">
                  {' '}
                  App Approval Checklist{' '}
                </Typography>
              </Box>
              <Box mb={8}>
                <Typography variant="body" align="center">
                  SurveyMonkey has strict standards for publishing public apps. Meet the criteria below and we&apos;ll
                  help promote your app in our <a href="/apps">App Directory</a>.
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="cardTitle" component="h3">
                  Basic Requirements
                </Typography>
              </Box>

              {renderAppChecklist()}

              <Box mb={2} mt={4}>
                <Typography variant="cardTitle" component="h3">
                  Creating a Listing
                </Typography>
              </Box>

              {renderListingChecklist()}

              <Box mt={3}>
                <Typography variant="body">NOTES:</Typography>
              </Box>
              <Box>
                <Typography variant="body">
                  SurveyMonkey retains the right to reject an app based on criteria not listed above. Approval of your
                  app by SurveyMonkey does not guarantee its compliance with the criteria above.
                </Typography>
              </Box>
            </Box>
          </Box>
        </div>
      </DeveloperWebBasePage>
    </div>
  );
};

export default BuildAPublicApp;
